import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../utils/api";
import { getSocket } from "../utils/socket";
import { t } from "../utils/i18n";
import { CameraIcon } from "../components/messages/icons";
import { dateActivityByKey } from "../utils/dateActivities";
import { FAST_CATEGORIES, LightningIcon } from "../components/FastCategoryIcons";
import { ACCENT, FAST_COLOR } from "../styles/tokens";
import { DEMANDE_TYPES, CONNECTION_TYPES, PRESTATION_TYPES, PAIEMENT_TYPES } from "../utils/notificationBuckets";
import { useIsMobile } from "../hooks/useIsMobile";

const FriendRequestIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/>
    <line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/>
  </svg>
);
const FriendAcceptedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/>
    <polyline points="17 11 19 13 23 9"/>
  </svg>
);
const MessageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.4 8.4 0 0 1-8.4 8.4 8.3 8.3 0 0 1-3.8-.9L3 21l1.9-5.8a8.3 8.3 0 0 1-.9-3.8A8.4 8.4 0 0 1 12.5 3a8.4 8.4 0 0 1 8.4 8.4z"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><polyline points="8.5 12.5 11 15 15.5 9.5"/>
  </svg>
);
// Boutons accepter/refuser circulaires — remplace les glyphes texte "✕"/"✓" (pas de vrai SVG)
// utilisés jusqu'ici pour date_invitation et pro_event_collab_invite.
const SmallXIcon = ({ color = "#666" }: { color?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);
const SmallCheckIcon = ({ color = "white" }: { color?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
);
const StarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const InviteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 7l10 6 10-6"/>
  </svg>
);
const HeartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/>
  </svg>
);
const GiftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="8" width="18" height="4" rx="1"/><rect x="5" y="12" width="14" height="9" rx="1"/>
    <path d="M12 8v13M12 8C10 4 6 4 6 6.5S9 8 12 8zM12 8c2-4 6-4 6-1.5S15 8 12 8z"/>
  </svg>
);
const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);
const NearbyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="2.5"/>
  </svg>
);
const VenueIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10.5 12 3l9 7.5"/><path d="M5.5 9.8V20h13V9.8"/><path d="M10 20v-5.5h4V20"/>
  </svg>
);
// Même style que BellBigIcon (48px, trait fin gris clair) — utilisée pour l'état vide de l'onglet
// "Visites" afin que les 4 sections de Notifications partagent la même identité visuelle
// d'icône vide, au lieu du petit EyeIcon noir (taille et couleur différentes des 3 autres).
const EyeBigIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const BellBigIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);
const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4"/><path d="M16 3v4"/><path d="M3 10h18"/>
  </svg>
);
// Une demande de prestation vient d'être publiée près de vous — même éclair que l'identité
// visuelle Power ailleurs dans l'app.
const PowerOpportunityIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 3 14h7l-1 8 11-14h-7l1-6z"/>
  </svg>
);
// Nouvelles icônes couvrant les familles ajoutées lors de l'inventaire complet des types de
// notification (rituels/communautés, réservations, paiements, alertes) — jusqu'ici ces types
// retombaient tous sur BellIcon faute d'entrée dédiée dans ICONS.
const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const EuroIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6.5A6.5 6.5 0 0 0 8 13"/><path d="M8 13a6.5 6.5 0 0 0 10 5.5"/><line x1="3" y1="10" x2="14" y2="10"/><line x1="3" y1="15" x2="12" y2="15"/>
  </svg>
);
const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const PhoneMissedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 1 17 1 17 7"/><line x1="23" y1="1" x2="17" y2="7"/>
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .3 2 .7 2.9a2 2 0 0 1-.4 2.1L8 10a16 16 0 0 0 6 6l1.3-1.4a2 2 0 0 1 2.1-.4c.9.4 1.9.6 2.9.7a2 2 0 0 1 1.7 2.1z"/>
  </svg>
);
const SparkleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8"/>
  </svg>
);

// Icône du badge d'une notification "fast_arrival" — la même identité catégorie que sur le FAST
// lui-même (voir FAST_CATEGORIES, components/FastCategoryIcons.tsx), plus jamais un emoji plaqué
// dans le titre (voir utils/fastNotify.ts côté serveur). Repli sur l'éclair générique FAST si la
// catégorie manque (anciennes notifications déjà en base, envoyées avant ce correctif).
const getNotifIcon = (n: any): (() => React.ReactElement) => {
  if (n.type === "fast_arrival") {
    const cat = FAST_CATEGORIES.find(c => c.key === n.data?.category);
    return cat ? () => <cat.Icon color={FAST_COLOR} /> : () => <LightningIcon color={FAST_COLOR} />;
  }
  return ICONS[n.type] || BellIcon;
};

const ICONS: Record<string, () => React.ReactElement> = {
  friend_request: FriendRequestIcon,
  friend_accepted: FriendAcceptedIcon,
  message: MessageIcon,
  comment: MessageIcon,
  comment_reply: MessageIcon,
  participation: CheckIcon,
  participation_accepted: CheckIcon,
  participation_rejected: BellIcon,
  rating: StarIcon,
  rate_participants: StarIcon,
  invite: InviteIcon,
  like: HeartIcon,
  match: HeartIcon,
  date_invitation: CalendarIcon,
  date_accepted: CheckIcon,
  story_like: HeartIcon,
  photo_like: HeartIcon,
  new_real: CameraIcon,
  user_joined_nearby: NearbyIcon,
  waitlist_spot_open: BellIcon,
  catalog_proposal_approved: StarIcon,
  catalog_proposal_rejected: BellIcon,
  venue_activity: VenueIcon,
  avatar_reminder: CameraIcon,
  referral_joined: FriendAcceptedIcon,
  power_request_nearby: PowerOpportunityIcon,
  birthday_upcoming: GiftIcon,
  birthday_today: GiftIcon,
  birthday_event_invite: GiftIcon,
  birthday_event_cancelled: GiftIcon,
  birthday_event_reminder: GiftIcon,
  birthday_event_revealed: GiftIcon,
  pro_event_collab_invite: VenueIcon,
  pro_event_collab_accepted: CheckIcon,
  venue_points_credited: StarIcon,
  venue_item_points_purchase: StarIcon,
  // Rituels / communautés
  ritual_join_request: UsersIcon,
  ritual_request_approved: CheckIcon,
  community_join_request: UsersIcon,
  community_request_approved: CheckIcon,
  // Réservations (bookings personnels + lieux)
  booking_request: CalendarIcon,
  booking_confirmed: CheckIcon,
  booking_cancelled: BellIcon,
  booking_refunded: EuroIcon,
  venue_booking_request: CalendarIcon,
  venue_booking_confirmed: CheckIcon,
  venue_booking_cancelled: BellIcon,
  // Devis / offres Power
  offer_accepted: CheckIcon,
  offer_declined: BellIcon,
  // Lieux — revendication de gestion
  venue_claim_approved: CheckIcon,
  venue_claim_rejected: WarningIcon,
  // Paiements
  payment_released: EuroIcon,
  payment_disputed: WarningIcon,
  payment_refunded: EuroIcon,
  item_sold: EuroIcon,
  // Compte / social / système
  mention: MessageIcon,
  welcome: SparkleIcon,
  avatar_rejected: WarningIcon,
  verification_approved: CheckIcon,
  verification_rejected: WarningIcon,
  daily_reminder: CalendarIcon,
  publication_cancelled: BellIcon,
  move_on_my_way: NearbyIcon,
  missed_call: PhoneMissedIcon,
};

// Regroupement des types de notifications existants en onglets (DEMANDE_TYPES/LIKE_TYPES, voir
// utils/notificationBuckets.ts — réutilisé tel quel par le panneau d'aperçu de la cloche,
// components/Layout.tsx, pour ne pas dupliquer cette liste à deux endroits). "Visites" reste à
// part : ce n'est pas une notification (jamais une par visite, volontairement — voir le
// commentaire dans profile_visits côté backend), mais une liste consultée à la demande.

// Titre/texte des 3 nouveaux types (Match/Date) reconstruits côté client depuis `data`, pour
// être réellement traduits dans les 12 langues — contrairement au title/body stocké en base
// (français, conservé seulement pour le fallback des notifications push). Tous les autres
// types gardent leur title/body existant tel quel.
const renderNotifText = (n: any, lang: string): { title: string; body: string } => {
  const name = n.data?.from_user_name || "";
  if (n.type === "match") return { title: t(lang, "notif_match_title"), body: t(lang, "notif_match_body_p1") + name + t(lang, "notif_match_body_p2") };
  if (n.type === "date_invitation") return { title: t(lang, "notif_date_invitation_title"), body: name + t(lang, "notif_date_invitation_body_p2") };
  if (n.type === "date_accepted") return { title: t(lang, "notif_date_accepted_title"), body: name + t(lang, "notif_date_accepted_body_p2") };
  if (n.type === "birthday_upcoming") return { title: t(lang, "notif_birthday_upcoming_title"), body: name + t(lang, n.data?.days === 7 ? "notif_birthday_upcoming_body_7_p2" : "notif_birthday_upcoming_body_30_p2") };
  if (n.type === "birthday_today") return { title: t(lang, "notif_birthday_today_title"), body: name + t(lang, "notif_birthday_today_body_p2") };
  if (n.type === "pro_event_collab_invite") return { title: t(lang, "notif_collab_invite_title"), body: (n.data?.from_venue_name || "") + t(lang, "notif_collab_invite_body_p2") };
  if (n.type === "pro_event_collab_accepted") return { title: t(lang, "notif_collab_accepted_title"), body: (n.data?.from_venue_name || "") + t(lang, "notif_collab_accepted_body_p2") };
  if (n.type === "venue_points_credited") return { title: t(lang, "notif_points_credited_title"), body: (n.data?.venue_name || "") + t(lang, "notif_points_credited_body_p2").replace("{n}", String(n.data?.amount ?? "")) };
  return { title: n.title, body: n.body };
};

const timeAgo = (dateStr?: string) => {
  if (!dateStr) return "";
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "À l'instant";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}j`;
};

export default function Notifications() {
  const { token, lang } = useApp();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState<any[]>([]);
  const [unreadIds, setUnreadIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<"activites" | "demandes" | "connexions" | "prestations" | "paiements" | "visites">(
    (["activites", "demandes", "connexions", "prestations", "paiements", "visites"] as const).includes(tabFromUrl as any) ? (tabFromUrl as any) : "activites"
  );
  // Le badge "1"/"2" d'un onglet (Demandes, Likes...) doit rester visible tant qu'on n'a pas
  // vraiment ouvert cet onglet précis — la page s'ouvrant toujours sur "Activités", c'est le seul
  // moyen de savoir dans QUEL onglet se trouve la vraie nouveauté qui a fait sonner la cloche
  // principale. Un Set plutôt qu'un booléen unique : chaque onglet garde son badge indépendamment
  // des autres, y compris celui ouvert par défaut au chargement.
  const [visitedTabs, setVisitedTabs] = useState<Set<typeof activeTab>>(() => new Set([activeTab]));
  const visitTab = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setVisitedTabs(prev => (prev.has(tab) ? prev : new Set(prev).add(tab)));
  };
  const [visitors, setVisitors] = useState<any[]>([]);
  const [visitorsLoaded, setVisitorsLoaded] = useState(false);
  const [loadingVisitors, setLoadingVisitors] = useState(false);
  // Résultat local d'une réponse à une invitation de rendez-vous (accepter/refuser), pour faire
  // disparaître/évoluer les boutons Accepter/Refuser sans recharger toute la liste.
  const [invitationResults, setInvitationResults] = useState<Record<string, { status: "busy" | "accepted" | "declined"; conversation_id?: string }>>({});

  const respondInvitation = (e: React.MouseEvent, invitationId: string, action: "accept" | "decline") => {
    e.stopPropagation();
    setInvitationResults(prev => ({ ...prev, [invitationId]: { status: "busy" } }));
    api.post(`/dates/invitations/${invitationId}/${action}`, {}).then(data => {
      if (data?.code) {
        setInvitationResults(prev => { const next = { ...prev }; delete next[invitationId]; return next; });
        return;
      }
      setInvitationResults(prev => ({ ...prev, [invitationId]: { status: action === "accept" ? "accepted" : "declined", conversation_id: data?.conversation_id } }));
    }).catch(() => {
      setInvitationResults(prev => { const next = { ...prev }; delete next[invitationId]; return next; });
    });
  };

  // Même principe que invitationResults, pour les invitations à co-organiser un Pro Event
  // (voir PUT /publications/collaborators/:id/accept|decline).
  const [collabResults, setCollabResults] = useState<Record<string, "busy" | "accepted" | "declined">>({});
  const respondCollab = (e: React.MouseEvent, collabId: string, action: "accept" | "decline") => {
    e.stopPropagation();
    setCollabResults(prev => ({ ...prev, [collabId]: "busy" }));
    api.put(`/publications/collaborators/${collabId}/${action}`, {}).then(data => {
      if (!data?.collaborator) { setCollabResults(prev => { const next = { ...prev }; delete next[collabId]; return next; }); return; }
      setCollabResults(prev => ({ ...prev, [collabId]: action === "accept" ? "accepted" : "declined" }));
    }).catch(() => {
      setCollabResults(prev => { const next = { ...prev }; delete next[collabId]; return next; });
    });
  };

  // Voeux d'anniversaire déjà envoyés cette session (par id de destinataire) — pour figer le
  // bouton "Inviter à sortir" en "Message envoyé" sans recharger toute la liste.
  const [birthdayWishSent, setBirthdayWishSent] = useState<Record<string, string>>({});
  const [sendingBirthdayWish, setSendingBirthdayWish] = useState<string | null>(null);
  const sendBirthdayWish = (e: React.MouseEvent, friendId: string) => {
    e.stopPropagation();
    if (sendingBirthdayWish) return;
    setSendingBirthdayWish(friendId);
    api.post(`/users/${friendId}/birthday-wish`, {}).then(data => {
      if (data?.conversationId) setBirthdayWishSent(prev => ({ ...prev, [friendId]: data.conversationId }));
    }).catch(() => {}).finally(() => setSendingBirthdayWish(null));
  };


  useEffect(() => {
    api.get("/notifications")
      .then(data => {
        const list = data?.notifications || [];
        setNotifs(list);
        // On garde en mémoire (côté écran) lesquelles étaient non lues à l'ouverture,
        // pour les distinguer visuellement, avant de toutes les marquer comme lues.
        setUnreadIds(new Set(list.filter((n: any) => !n.is_read).map((n: any) => n.id)));
        if (list.some((n: any) => !n.is_read)) {
          api.put("/notifications/read-all", {}).catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!token) return;
    const socket = getSocket(token);
    const onNew = (notif: any) => setNotifs(prev => [notif, ...prev.filter(n => n.id !== notif.id)]);
    socket.on("new_notification", onNew);
    return () => { socket.off("new_notification", onNew); };
  }, [token]);

  useEffect(() => {
    if (activeTab !== "visites" || visitorsLoaded) return;
    setLoadingVisitors(true);
    api.get("/users/me/visitors")
      .then(data => setVisitors(data?.visitors || []))
      .catch(() => {})
      .finally(() => { setLoadingVisitors(false); setVisitorsLoaded(true); });
  }, [activeTab, visitorsLoaded]);

  const handleClick = (notif: any) => {
    const data = notif.data || {};
    if (notif.type === "message" && data.conversationId) navigate("/messages");
    else if (notif.type === "rate_participants") navigate("/rating");
    else if (notif.type === "invite" && data.target_type && data.target_id) navigate(`/${data.target_type}/${data.target_id}`);
    else if (["participation", "participation_accepted", "participation_rejected", "waitlist_spot_open", "comment", "comment_reply"].includes(notif.type) && data.publication_id) navigate(`/publication/${data.publication_id}`);
    else if (["booking_request", "booking_confirmed", "booking_cancelled", "booking_refunded", "venue_booking_request", "venue_booking_confirmed", "venue_booking_cancelled"].includes(notif.type)) navigate("/bookings");
    else if (notif.type === "power_offer" || notif.type === "offer_accepted" || notif.type === "offer_declined" || notif.type === "offer_negotiation" || notif.type === "power_request_nearby") navigate("/power-requests");
    else if (notif.type === "friend_request") navigate("/profile?tab=Amis&requests=received");
    else if (["ritual_join_request", "ritual_request_approved"].includes(notif.type) && data.ritual_id) navigate(`/ritual/${data.ritual_id}`);
    else if (["community_join_request", "community_request_approved"].includes(notif.type) && data.community_id) navigate(`/community/${data.community_id}`);
    else if (["venue_claim_approved", "venue_claim_rejected"].includes(notif.type)) navigate("/my-venues");
    else if (notif.type === "item_sold") navigate("/settings", { state: { openPayments: "transactions" } });
    else if (["payment_released", "payment_disputed", "payment_refunded"].includes(notif.type)) navigate("/settings", { state: { openPayments: "earnings" } });
    else if (notif.type === "mention" && data.publication_id) navigate(`/publication/${data.publication_id}`);
    else if (notif.type === "welcome") navigate("/profile");
    else if (notif.type === "avatar_rejected") navigate("/profile");
    else if (notif.type === "publication_cancelled") navigate("/profile");
    else if (notif.type === "move_on_my_way" && data.publication_id) navigate(`/publication/${data.publication_id}`);
    else if (notif.type === "missed_call" && data.conversationId) navigate(`/messages/${data.conversationId}`);
    else if (notif.type === "photo_like" && data.photo_id) navigate(`/profile?tab=Galerie&photo=${data.photo_id}`);
    else if (notif.type === "new_real" && data.story_id) navigate("/?real=" + data.story_id);
    else if (["friend_accepted", "like", "story_like", "user_joined_nearby", "birthday_today"].includes(notif.type) && data.from_user_id) navigate(`/profile/${data.from_user_id}`);
    else if (notif.type === "birthday_upcoming" && data.from_user_id) navigate(`/birthday-events/create?birthday_friend_id=${data.from_user_id}&birthday_friend_name=${encodeURIComponent(data.from_user_name || "")}`);
    else if (["birthday_event_invite", "birthday_event_cancelled", "birthday_event_reminder", "birthday_event_revealed"].includes(notif.type) && data.event_id) navigate(`/birthday-events/${data.event_id}`);
    else if (notif.type === "match" && data.from_user_id) navigate(`/profile/${data.from_user_id}`, { state: { openDateSheet: true } });
    else if (notif.type === "date_invitation" && data.from_user_id) navigate(`/profile/${data.from_user_id}`);
    else if (notif.type === "date_accepted" && data.conversation_id) navigate(`/messages/${data.conversation_id}`);
    else if (["catalog_proposal_approved", "catalog_proposal_rejected"].includes(notif.type)) navigate("/profile");
    else if (notif.type === "venue_activity" && data.venue_id) navigate(`/venue/${data.venue_id}`);
    else if (notif.type === "avatar_reminder") navigate("/profile");
    else if (notif.type === "referral_joined") navigate("/invite");
    else if (notif.type === "verification_rejected") navigate("/verify-identity");
    else if (notif.type === "verification_approved") navigate("/profile");
  };

  const goToProfile = (e: React.MouseEvent, userId?: string) => {
    if (!userId) return;
    e.stopPropagation();
    navigate(`/profile/${userId}`);
  };

  const goToRequests = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate("/profile?tab=Amis&requests=received");
  };

  const goToPhoto = (e: React.MouseEvent, photoId?: string) => {
    if (!photoId) return;
    e.stopPropagation();
    navigate(`/profile?tab=Galerie&photo=${photoId}`);
  };

  const markAllRead = () => {
    setUnreadIds(new Set());
    api.put("/notifications/read-all", {}).catch(() => {});
  };

  const unreadCount = unreadIds.size;
  const demandesNotifs = notifs.filter(n => DEMANDE_TYPES.includes(n.type));
  const connexionsNotifs = notifs.filter(n => CONNECTION_TYPES.includes(n.type));
  const prestationsNotifs = notifs.filter(n => PRESTATION_TYPES.includes(n.type));
  const paiementsNotifs = notifs.filter(n => PAIEMENT_TYPES.includes(n.type));
  const bucketedTypes = new Set([...DEMANDE_TYPES, ...CONNECTION_TYPES, ...PRESTATION_TYPES, ...PAIEMENT_TYPES]);
  const activitesNotifs = notifs.filter(n => !bucketedTypes.has(n.type));
  const filteredNotifs =
    activeTab === "demandes" ? demandesNotifs :
    activeTab === "connexions" ? connexionsNotifs :
    activeTab === "prestations" ? prestationsNotifs :
    activeTab === "paiements" ? paiementsNotifs :
    activitesNotifs;

  const summary = [
    { label: t(lang, "notif_unread"), count: unreadCount },
    { label: t(lang, "notif_tab_requests"), count: demandesNotifs.length },
    { label: t(lang, "notif_tab_connections"), count: connexionsNotifs.length },
    { label: t(lang, "notif_tab_prestations"), count: prestationsNotifs.length },
    { label: t(lang, "notif_tab_payments"), count: paiementsNotifs.length },
    { label: t(lang, "messages"), count: notifs.filter(n => n.type === "message").length },
  ];

  // Nombre de non-lues par onglet, calculé à partir du même snapshot `unreadIds` (pris avant le
  // marquage read-all) — pour que l'utilisateur voie tout de suite QUEL onglet contient la
  // nouveauté qui vient de déclencher le badge sur la cloche, sans avoir à cliquer sur chacun.
  // 0 pour un onglet déjà visité : le badge doit disparaître dès qu'on est allé le consulter,
  // pas seulement au premier chargement (voir visitedTabs plus haut).
  const unreadInTab: Record<typeof activeTab, number> = {
    activites: visitedTabs.has("activites") ? 0 : activitesNotifs.filter(n => unreadIds.has(n.id)).length,
    demandes: visitedTabs.has("demandes") ? 0 : demandesNotifs.filter(n => unreadIds.has(n.id)).length,
    connexions: visitedTabs.has("connexions") ? 0 : connexionsNotifs.filter(n => unreadIds.has(n.id)).length,
    prestations: visitedTabs.has("prestations") ? 0 : prestationsNotifs.filter(n => unreadIds.has(n.id)).length,
    paiements: visitedTabs.has("paiements") ? 0 : paiementsNotifs.filter(n => unreadIds.has(n.id)).length,
    visites: 0,
  };

  const TABS: { key: typeof activeTab; label: string }[] = [
    { key: "activites", label: t(lang, "notif_tab_activities") },
    { key: "demandes", label: t(lang, "notif_tab_requests") },
    { key: "connexions", label: t(lang, "notif_tab_connections") },
    { key: "prestations", label: t(lang, "notif_tab_prestations") },
    { key: "paiements", label: t(lang, "notif_tab_payments") },
    { key: "visites", label: t(lang, "notif_tab_visits") },
  ];

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* overflowX explicite : sans lui la promotion CSS de overflowY:"auto" transforme
          automatiquement overflowX en "auto" aussi (règle du spec), ce qui a permis à la barre
          d'onglets ci-dessous de faire défiler TOUTE la page horizontalement le jour où 6 onglets
          n'ont plus tenu sur un écran mobile — jamais voulu, une page de notifications ne doit
          jamais défiler dans les deux sens à la fois. */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #F0F0F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 22, fontWeight: 700 }}>{t(lang, "notif_title")}</span>
          {notifs.length > 0 && unreadCount > 0 && (
            <button onClick={markAllRead} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>{t(lang, "notif_mark_all")}</button>
          )}
        </div>

        {/* Onglets — mêmes notifications qu'avant pour Activités/Demandes/Likes, juste triées
            différemment (voir DEMANDE_TYPES/LIKE_TYPES) ; Visites est une source à part
            (GET /users/me/visitors, pas la table notifications).
            6 onglets ne tiennent plus tous sur un écran mobile avec des libellés complets
            ("Prestations", "Connexions"...) : plutôt que de les rétrécir jusqu'à l'illisible,
            la bande d'onglets devient son propre petit ruban défilant horizontalement (comme les
            chips de filtre d'Explorer) — chaque bouton garde sa largeur naturelle
            (flexShrink: 0), c'est SEULEMENT ce ruban qui scrolle, jamais la page entière. */}
        <div className="notif-tabs-scroll" style={{ display: "flex", overflowX: "auto", borderBottom: "1px solid #F0F0F0", backgroundColor: "white", position: "sticky", top: 0, zIndex: 5 }}>
          <style>{`.notif-tabs-scroll::-webkit-scrollbar { display: none; } .notif-tabs-scroll { scrollbar-width: none; }`}</style>
          {TABS.map(tabDef => (
            <button key={tabDef.key} onClick={() => visitTab(tabDef.key)} style={{
              flexShrink: 0, whiteSpace: "nowrap", padding: "13px 14px", border: "none", cursor: "pointer", backgroundColor: "transparent",
              borderBottom: activeTab === tabDef.key ? "2px solid #0a0a0a" : "2px solid transparent",
              color: activeTab === tabDef.key ? "#0a0a0a" : "#999",
              fontWeight: activeTab === tabDef.key ? 700 : 400, fontSize: 13,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              {tabDef.label}
              {unreadInTab[tabDef.key] > 0 && (
                <span style={{ minWidth: 16, height: 16, borderRadius: 8, backgroundColor: "#0a0a0a", color: "white", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px" }}>
                  {unreadInTab[tabDef.key]}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading && activeTab !== "visites" && <div style={{ textAlign: "center", padding: 60, color: "#999" }}>{t(lang, "loading_ellipsis")}</div>}

        {activeTab === "visites" ? (
          loadingVisitors ? (
            <div style={{ textAlign: "center", padding: 60, color: "#999" }}>{t(lang, "loading_ellipsis")}</div>
          ) : visitors.length === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 20px", textAlign: "center" }}>
              <div style={{ marginBottom: 20 }}><EyeBigIcon /></div>
              <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 12 }}>{t(lang, "notif_nobody_yet")}</h2>
              <p style={{ fontSize: 15, color: "#666", lineHeight: 1.7, maxWidth: 400 }}>{t(lang, "notif_visitors_empty")}</p>
            </div>
          ) : (
            <div>
              {visitors.map(v => (
                <div key={v.id} onClick={() => navigate(`/profile/${v.id}`)} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 24px", cursor: "pointer", borderBottom: "1px solid #F5F5F5" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", backgroundColor: "#F0F0F0", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#666" }}>
                    {v.avatar_url ? <img src={v.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (v.name || "?")[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600 }}>{v.name}</div>
                  <div style={{ fontSize: 11, color: "#999", flexShrink: 0 }}>{timeAgo(v.created_at)}</div>
                </div>
              ))}
            </div>
          )
        ) : !loading && filteredNotifs.length === 0 && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 20px", textAlign: "center" }}>
            <div style={{ marginBottom: 20 }}><BellBigIcon /></div>
            <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 12 }}>{activeTab === "activites" ? t(lang, "notif_empty") : t(lang, "notif_nothing_yet")}</h2>
            <p style={{ fontSize: 15, color: "#666", lineHeight: 1.7, maxWidth: 400, marginBottom: 24 }}>
              {activeTab === "demandes" ? t(lang, "notif_requests_empty")
                : activeTab === "connexions" ? t(lang, "notif_likes_empty")
                : activeTab === "prestations" ? t(lang, "notif_prestations_empty")
                : activeTab === "paiements" ? t(lang, "notif_payments_empty")
                : t(lang, "notif_activities_empty")}
            </p>
          </div>
        )}

        {activeTab !== "visites" && !loading && filteredNotifs.length > 0 && (
          <div>
            {filteredNotifs.map(n => {
              const Icon = getNotifIcon(n);
              const avatar = n.data?.from_user_avatar;
              const isUnread = unreadIds.has(n.id);
              const isFast = n.type === "fast_arrival";
              const notifText = renderNotifText(n, lang);
              const invitationId: string | undefined = n.data?.invitation_id;
              const invitationResult = invitationId ? invitationResults[invitationId] : undefined;
              const isExpired = n.type === "date_invitation" && n.data?.expires_at && new Date(n.data.expires_at).getTime() < Date.now();
              const activity = n.data?.activity_key ? dateActivityByKey(n.data.activity_key) : undefined;
              return (
                <div key={n.id} onClick={() => handleClick(n)} style={{
                  display: "flex", gap: 14, padding: "16px 24px 16px 21px", cursor: "pointer",
                  borderBottom: "1px solid #F5F5F5", backgroundColor: "white",
                  borderLeft: isUnread ? `3px solid ${ACCENT}` : "3px solid transparent",
                }}>
                  <div
                    onClick={e => goToProfile(e, n.data?.from_user_id)}
                    style={{ position: "relative", width: 40, height: 40, flexShrink: 0, cursor: avatar && n.data?.from_user_id ? "pointer" : "default" }}
                  >
                    {avatar ? (
                      <img src={avatar} alt="" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: isFast ? FAST_COLOR + "1F" : "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon />
                      </div>
                    )}
                    {avatar && (
                      <div style={{ position: "absolute", bottom: -2, right: -2, width: 20, height: 20, borderRadius: "50%", backgroundColor: isFast ? FAST_COLOR + "1F" : "#F0F0F0", boxShadow: "0 0 0 2px white", display: "flex", alignItems: "center", justifyContent: "center", transform: "scale(0.7)" }}>
                        <Icon />
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: isUnread ? 700 : 500, marginBottom: 3 }}>{notifText.title}</div>
                    <div style={{ fontSize: 13, color: "#666" }}>{notifText.body}</div>
                    {n.type === "date_invitation" && (
                      <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                        {activity && t(lang, activity.i18nKey)}{activity && n.data?.place_name ? " · " : ""}{n.data?.place_name}
                        {n.data?.scheduled_date && <> · {new Date(n.data.scheduled_date).toLocaleDateString(lang)}</>}
                        {n.data?.scheduled_time && <> · {n.data.scheduled_time}</>}
                      </div>
                    )}
                    {n.type === "date_invitation" && (
                      isExpired ? (
                        <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>{t(lang, "date_status_expired")}</div>
                      ) : invitationResult?.status === "declined" ? null : invitationResult?.status === "accepted" ? (
                        <button onClick={e => { e.stopPropagation(); navigate(`/messages/${invitationResult.conversation_id}`); }} style={{ marginTop: 8, fontSize: 11.5, fontWeight: 700, color: "white", background: "#0a0a0a", border: "none", borderRadius: 14, padding: "5px 12px", cursor: "pointer" }}>
                          {t(lang, "date_action_discuss")}
                        </button>
                      ) : invitationId && (
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                          <button
                            disabled={invitationResult?.status === "busy"}
                            onClick={e => respondInvitation(e, invitationId, "decline")}
                            style={{ width: 30, height: 30, borderRadius: 15, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#666", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          ><SmallXIcon /></button>
                          <button
                            disabled={invitationResult?.status === "busy"}
                            onClick={e => respondInvitation(e, invitationId, "accept")}
                            style={{ width: 30, height: 30, borderRadius: 15, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          ><SmallCheckIcon /></button>
                        </div>
                      )
                    )}
                    {n.type === "match" && (
                      <button onClick={e => { e.stopPropagation(); navigate(`/profile/${n.data?.from_user_id}`, { state: { openDateSheet: true } }); }} style={{ marginTop: 8, fontSize: 11.5, fontWeight: 700, color: "white", background: "#0a0a0a", border: "none", borderRadius: 14, padding: "5px 12px", cursor: "pointer", textTransform: "uppercase" }}>
                        {t(lang, "like_btn_date")}
                      </button>
                    )}
                    {n.type === "date_accepted" && n.data?.conversation_id && (
                      <button onClick={e => { e.stopPropagation(); navigate(`/messages/${n.data.conversation_id}`); }} style={{ marginTop: 8, fontSize: 11.5, fontWeight: 700, color: "white", background: "#0a0a0a", border: "none", borderRadius: 14, padding: "5px 12px", cursor: "pointer" }}>
                        {t(lang, "date_action_discuss")}
                      </button>
                    )}
                    {/* Invitation à co-organiser un Pro Event — même mécanique Accepter/Refuser que
                        date_invitation ci-dessus (voir PUT /publications/collaborators/:id/accept|decline). */}
                    {n.type === "pro_event_collab_invite" && n.data?.collaborator_id && (
                      collabResults[n.data.collaborator_id] === "declined" ? null : collabResults[n.data.collaborator_id] === "accepted" ? (
                        <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>{t(lang, "date_status_accepted")}</div>
                      ) : (
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                          <button
                            disabled={collabResults[n.data.collaborator_id] === "busy"}
                            onClick={e => respondCollab(e, n.data.collaborator_id, "decline")}
                            style={{ width: 30, height: 30, borderRadius: 15, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#666", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          ><SmallXIcon /></button>
                          <button
                            disabled={collabResults[n.data.collaborator_id] === "busy"}
                            onClick={e => respondCollab(e, n.data.collaborator_id, "accept")}
                            style={{ width: 30, height: 30, borderRadius: 15, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          ><SmallCheckIcon /></button>
                        </div>
                      )
                    )}
                    {/* J-30/J-7 : direct sur Create, catégorie et invité déjà pré-remplis — le but
                        étant d'inciter à organiser plutôt que de laisser filer la notification. */}
                    {n.type === "birthday_upcoming" && n.data?.from_user_id && (
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/birthday-events/create?birthday_friend_id=${n.data.from_user_id}&birthday_friend_name=${encodeURIComponent(n.data.from_user_name || "")}`); }}
                        style={{ marginTop: 8, fontSize: 11.5, fontWeight: 700, color: "white", background: "#0a0a0a", border: "none", borderRadius: 14, padding: "5px 12px", cursor: "pointer" }}
                      >
                        {t(lang, "notif_birthday_action_organize")}
                      </button>
                    )}
                    {/* Jour J : envoie directement le message de voeux (POST .../birthday-wish),
                        puis bascule sur "Message envoyé" pour enchaîner sans repasser par la notif. */}
                    {n.type === "birthday_today" && n.data?.from_user_id && (
                      birthdayWishSent[n.data.from_user_id] ? (
                        <button onClick={e => { e.stopPropagation(); navigate(`/messages/${birthdayWishSent[n.data.from_user_id]}`); }} style={{ marginTop: 8, fontSize: 11.5, fontWeight: 700, color: "#0a0a0a", background: "white", border: "1px solid #E0E0E0", borderRadius: 14, padding: "5px 12px", cursor: "pointer" }}>
                          {t(lang, "notif_birthday_wish_sent")}
                        </button>
                      ) : (
                        <button
                          disabled={sendingBirthdayWish === n.data.from_user_id}
                          onClick={e => sendBirthdayWish(e, n.data.from_user_id)}
                          style={{ marginTop: 8, fontSize: 11.5, fontWeight: 700, color: "white", background: "#0a0a0a", border: "none", borderRadius: 14, padding: "5px 12px", cursor: "pointer", opacity: sendingBirthdayWish === n.data.from_user_id ? 0.6 : 1 }}
                        >
                          {t(lang, "notif_birthday_action_invite")}
                        </button>
                      )
                    )}
                    {n.type === "user_joined_nearby" && (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                        {n.data?.city && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, color: "#999" }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="2.5"/></svg>
                            {n.data.city}
                          </span>
                        )}
                        {n.data?.from_user_id && (
                          <button onClick={e => goToProfile(e, n.data.from_user_id)} style={{ fontSize: 11.5, fontWeight: 700, color: "white", background: "#0a0a0a", border: "none", borderRadius: 14, padding: "5px 12px", cursor: "pointer" }}>
                            {t(lang, "see_profile")}
                          </button>
                        )}
                      </div>
                    )}
                    {n.type === "friend_request" && (
                      <button onClick={goToRequests} style={{ marginTop: 8, fontSize: 11.5, fontWeight: 700, color: "white", background: "#0a0a0a", border: "none", borderRadius: 14, padding: "5px 12px", cursor: "pointer" }}>
                        {t(lang, "notif_see_request")}
                      </button>
                    )}
                  </div>
                  {n.type === "photo_like" && n.data?.photo_url && (
                    <div onClick={e => goToPhoto(e, n.data.photo_id)} style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", flexShrink: 0, cursor: "pointer" }}>
                      <img src={n.data.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: "#999", flexShrink: 0, whiteSpace: "nowrap" }}>{timeAgo(n.created_at)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sidebar — masquée en mobile pour laisser toute la place à la liste */}
      {!isMobile && (
        <div style={{ width: 280, flexShrink: 0, borderLeft: "1px solid #F0F0F0", padding: 20, overflowY: "auto" }}>
          <div style={{ backgroundColor: "white", borderRadius: 16, padding: 16, border: "1px solid #F0F0F0" }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{t(lang, "notif_summary")}</div>
            {summary.map((item, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < summary.length - 1 ? "1px solid #F8F8F8" : "none" }}>
                <span style={{ fontSize: 13, color: "#666" }}>{item.label}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: item.count > 0 ? "#0a0a0a" : "#ccc" }}>{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
